import 'leaflet/dist/leaflet.css';

import { Box } from '@mui/material';
import Leaflet from 'leaflet';
import { Fragment } from 'react';
import { Marker, Polyline, TileLayer, MapContainer, Tooltip } from 'react-leaflet';
import MarkerClusterGroup from 'src/components/MarkerClusterGroup';
import { PlanningsMapGaragePositionEntity } from 'src/entities/PlanningsMapGaragePosition.entity';
import { PositionEntity } from 'src/entities/PositionEntity';
import PlanningMapCurrentPositionsPresenter from 'src/pages/V3Plans/presenters/PlanningMapCurrentPositions.presenter';
import PlanningMapGarageMarkersPresenter from 'src/pages/V3Plans/presenters/PlanningMapGarageMarkers.presenter';

import { forwardClusterEvent } from '../clusterEvents';
import { DISPLAY_KIND_OPTIONS, showsGarage, showsLoading, showsUnloading } from '../mapDisplayKinds';
import { markerOpacity, UNASSIGNED_PIN } from '../mapMarkerStyles';
import { MapRouteMarkerVM, MapUnassignedMarkerVM, MapVehicleRouteVM } from '../mapVM';
import { buildRouteMarkerEventHandlers, buildUnassignedMarkerEventHandlers } from '../markerEvents';
import { UNASSIGNED_POLYLINE_COLOR, UnassignedPolyline } from '../unassignedPolylines';
import { UnassignedTooltipVM } from '../unassignedTooltip';
import { resolveVisibleRoutes } from '../visibleRoutes';


import MapBackgroundClick from './MapBackgroundClick.presenter';
import MapFitBounds from './MapFitBounds.presenter';
import MapInvalidateSize from './MapInvalidateSize.presenter';

type Props = {
  routes: MapVehicleRouteVM[];
  unassignedMarkers: MapUnassignedMarkerVM[];
  /** 選択中の未割当案件の 積地→降地 の線。空なら描かない */
  unassignedPolylines?: UnassignedPolyline[];
  /** 右ペインの開閉状態。変わったら地図のサイズを取り直す */
  rightPanelOpen?: boolean;
  /** 地図に出す種類（積地 / 納品地 / 車庫）。未指定なら全部出す */
  displayKinds?: readonly string[];
  /** 車両の現在地を出すか。車両位置のライセンスがある会社だけ true になりうる */
  showTruckStatus?: boolean;
  /** 現在地の取得範囲。showTruckStatus のときだけ使う */
  startOn?: string;
  endOn?: string;
  garages: PlanningsMapGaragePositionEntity[];
  selectedRowIds: Set<string>;
  onMarkerRowClick: (rowId: string) => void;
  /** マーカー・未割当ピンから詳細パネルを開く配線。 */
  onMarkerClick?: (rowId: string, marker: MapRouteMarkerVM) => void;
  onUnassignedClick?: (id: string) => void;
  /**
   * 未割当ピンのホバーに出す内容（id 引き）。詳細カードはクリックで出すので、
   * ホバーはこの軽い表示だけにする（サンプル MapView.tsx:186-224 と同じ切り分け）。
   */
  unassignedTooltips?: ReadonlyMap<string, UnassignedTooltipVM>;
  /** 右クリックで選択解除（旧地図と同じ操作） */
  onMarkerRowDeselect?: (rowId: string) => void;
  onUnassignedDeselect?: (id: string) => void;
  /** 地図の何も無いところが押されたとき。開いているパネルを閉じるのに使う */
  onBackgroundClick?: () => void;
  /** 選択中の未割当案件。未選択のピンを薄くするのに使う */
  selectedUnassignedIds?: ReadonlySet<string>;
  /** データ取得中は初回 fitBounds を待たせる（途中の一部だけで固定されるのを防ぐ） */
  isLoading?: boolean;
};

// 番号付き丸マーカー。color は車両色、n は配送順。
// 非選択を薄くする処理は廃止した（全体の配置が読み取れなくなるため。mapMarkerStyles 参照）
function numberedIcon(color: string, n: number) {
  const html = `<div style="background:${color};width:22px;height:22px;border-radius:50%;color:#fff;display:flex;align-items:center;justify-content:center;font-size:12px;border:2px solid #fff;">${n}</div>`;
  return Leaflet.divIcon({ className: 'map-vehicle-pin', iconAnchor: [11, 11], html });
}

// 旧地図と同じ PNG ピン。自前の 14px の丸だと、クラスターが解けた後に
// どこにあるか分からないという指摘があった（寸法は mapMarkerStyles が正本）
const unassignedIcon = new Leaflet.Icon({
  iconUrl: UNASSIGNED_PIN.iconUrl,
  iconSize: [...UNASSIGNED_PIN.iconSize],
  iconAnchor: [...UNASSIGNED_PIN.iconAnchor],
  tooltipAnchor: [...UNASSIGNED_PIN.tooltipAnchor],
  className: 'map-unassigned-pin',
});

const MapView = ({
  routes,
  unassignedMarkers,
  unassignedPolylines = [],
  rightPanelOpen,
  displayKinds,
  showTruckStatus,
  startOn,
  endOn,
  garages,
  selectedRowIds,
  onMarkerRowClick,
  onMarkerClick,
  onUnassignedClick,
  unassignedTooltips,
  onMarkerRowDeselect,
  onUnassignedDeselect,
  onBackgroundClick,
  selectedUnassignedIds,
  isLoading = false,
}: Props) => {
  // 未指定なら全部出す（フィルタを渡さない呼び出し元の既定）
  const kinds = displayKinds ?? DISPLAY_KIND_OPTIONS;
  // 未割当だけを選んだときも、配車済みの点は「選んでいない」ので薄くする
  const hasSelection = selectedRowIds.size > 0 || (selectedUnassignedIds?.size ?? 0) > 0;
  // 線は選択した車両のぶんだけ描く。全車両を重ねると経路が読めない（指示 47）
  const visibleRouteIds = new Set(resolveVisibleRoutes(routes, selectedRowIds).map((r) => r.rowId));

  // 初回 fitBounds の対象。ルート・未割当が無く車庫だけの日でも地図が寄るよう車庫座標も含める。
  const allPositions: PositionEntity[] = routes
    .flatMap((r) => r.positions)
    .concat(unassignedMarkers.map((m) => m.position))
    .concat(garages.map((g) => g.position));

  return (
    <MapContainer
      style={{ width: '100%', height: '100%' }}
      center={[35.6895, 139.6917]}
      zoom={11}
      scrollWheelZoom
      zoomControl={false}
    >
      <TileLayer
        attribution='&copy; <a href="http://osm.org/copyright">OpenStreetMap</a>'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />
      <MapFitBounds positions={allPositions} isReady={!isLoading} />
      {onBackgroundClick && <MapBackgroundClick onBackgroundClick={onBackgroundClick} />}
      {/* 右ペインを閉じて地図が広がっても、測り直さないと広がった分が空白になる */}
      <MapInvalidateSize watch={rightPanelOpen} />

      {routes.map((route) => {
        return (
          <Fragment key={route.rowId}>
            {visibleRouteIds.has(route.rowId) && route.positions.length >= 2 && (
              /* Why not 点線: 地図を縮めると破線の隙間が地点の間隔を超えて線が消え、
                 どの順で回るのかが読めなくなる（指摘 2026/08/18）。 */
              <Polyline
                positions={route.positions}
                pathOptions={{ color: route.color, opacity: 0.7 }}
              />
            )}
            {route.markers.filter((m) => (m.kind === 'loading' ? showsLoading(kinds) : showsUnloading(kinds))).map((m) => (
              <Marker
                key={`${route.rowId}-${m.seq}`}
                position={m.position}
                icon={numberedIcon(route.color, m.seq)}
                opacity={markerOpacity({ hasSelection, isSelected: selectedRowIds.has(route.rowId) })}
                eventHandlers={buildRouteMarkerEventHandlers(route.rowId, m, {
                    onMarkerRowClick, onMarkerClick, onMarkerRowDeselect,
                  })}
              />
            ))}
          </Fragment>
        );
      })}

      {/* 未割当案件ピンは /plans の OrderMarkers 同様クラスター化する（ルート番号マーカーは個別のまま）。 */}
      {/* クラスター設定は旧地図 (PlanningMapOrderMarkers.presenter.tsx:75-82) に合わせる。
          既定のままだとズームインしても個別のピンにばらけない。
          zoomToBoundsOnClick を切り、クラスターのクリック / 右クリックを
          配下マーカーの click / contextmenu へ流す（＝右リストの選択と連動する） */}
      <MarkerClusterGroup
        chunkedLoading
        animate={false}
        animateAddingMarkers={false}
        maxClusterRadius={(zoom: number) => 100 / zoom}
        zoomToBoundsOnClick={false}
        onClick={forwardClusterEvent('click')}
        onContextMenu={forwardClusterEvent('contextmenu')}
      >
        {unassignedMarkers.map((m) => {
          const tooltip = unassignedTooltips?.get(m.id);
          return (
            <Marker
              key={m.id}
              position={m.position}
              icon={unassignedIcon}
              opacity={markerOpacity({ hasSelection, isSelected: selectedUnassignedIds?.has(m.id) ?? false })}
              eventHandlers={buildUnassignedMarkerEventHandlers(m.id, {
                onUnassignedClick, onUnassignedDeselect,
              })}
            >
              {tooltip && (
                <Tooltip direction="top" offset={[0, -12]} opacity={1} className="unassigned-hover-tooltip">
                  <Box sx={{ minWidth: 200 }}>
                    <Box sx={{ fontSize: 12, fontWeight: 600, mb: 0.75 }}>{tooltip.title}</Box>
                    {tooltip.rows.map((row) => (
                      <Box key={row.label} sx={{ display: 'flex', gap: 1, fontSize: 11, lineHeight: 1.5 }}>
                        <Box component="span" sx={{ minWidth: 56, color: 'hsl(var(--muted-foreground))' }}>
                          {row.label}
                        </Box>
                        <Box component="span">{row.value}</Box>
                      </Box>
                    ))}
                  </Box>
                </Tooltip>
              )}
            </Marker>
          );
        })}
      </MarkerClusterGroup>

      {/* 選択した未割当案件の 積地→降地。点だけでは「どこからどこへ運ぶか」が読めない
          (V3Plans presenters/OrderPolylinePresenter.tsx:38-45 と同じ見た目) */}
      {unassignedPolylines.map((line) => (
        <Polyline
          key={`unassigned-${line.id}`}
          positions={line.positions}
          pathOptions={{ color: UNASSIGNED_POLYLINE_COLOR, weight: 5, opacity: 1 }}
        />
      ))}

      {/* 車庫は /plans と同じ presenter（グレーPNGピン＋実名Tooltip）で描画する。 */}
      {showsGarage(kinds) && <PlanningMapGarageMarkersPresenter entities={garages} />}

      {/* 車両の現在地。旧地図配車と同じ presenter を使う（V3Plans/Presenter.tsx:1116-1120） */}
      {showTruckStatus && startOn && endOn && (
        <PlanningMapCurrentPositionsPresenter startOn={startOn} endOn={endOn} />
      )}
    </MapContainer>
  );
};

export default MapView;
